import { InputHTMLAttributes, forwardRef, useId } from 'react'
import { clsx } from 'clsx'

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  helperText?: string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, helperText, id, ...props }, ref) => {
    const autoId = useId()
    const inputId = id ?? autoId
    const hintId = `${inputId}-hint`
    return (
      <div className="w-full">
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 mb-2">
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={error || helperText ? hintId : undefined}
          className={clsx(
            'w-full px-4 py-3 rounded-lg border-2 transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-offset-1',
            'disabled:bg-gray-100 disabled:cursor-not-allowed',
            'text-base', // Mobile-friendly text size
            {
              'border-gray-300 focus:border-accent-800 focus:ring-accent-300': !error,
              'border-primary-600 focus:border-primary-700 focus:ring-primary-200': error,
            },
            className
          )}
          {...props}
        />
        {error && (
          <p id={hintId} role="alert" className="mt-1 text-sm text-primary-800">{error}</p>
        )}
        {helperText && !error && (
          <p id={hintId} className="mt-1 text-sm text-gray-500">{helperText}</p>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'
